/**
 * Fetches a page of documents for any surface that lists them.
 *
 * Saved-documents and projects are each backed by a `Project` row, so they scope
 * through `project_id` rather than a list of ids: the backend resolves
 * membership, which matters because `ProjectDocument` rows spell a document as
 * either `str(Measure.id)` or the NMED docid and both are live in production.
 * Resolving client-side would have to pick one.
 *
 * Doc search owns no document set and passes no `projectId`, searching the whole
 * corpus the caller can reach. It is otherwise the same request, so it runs
 * through here too rather than keeping a second copy of the filter translation,
 * facet reading and abort handling.
 *
 * Results are mapped to `DocAiDocument` so the existing table renders unchanged.
 */

import type { MeasureDocumentAdapter } from '@/adapters/measure-document-adapter';
import type { DocAiDocument } from '@/types/api-response/doc-ai-types';
import type { DocumentFilter, DocumentSort } from '@/types/measure-document-types';
import type { FilterState, SortOrder } from '@/types/nmed/shared-filter-types';
import { reactive, ref } from 'vue';
import { createMeasureDocumentAdapter } from '@/adapters/measure-document-adapter';
import { filterStateToDocumentFilters } from '@/utils/filter-state-to-document-filters';
import { collectDimensionPaths, measureToDocAiDocument } from '@/utils/measure-to-doc-ai';
import { toSortField } from '@/utils/schema-field-key';

/* ---------------------------------------------------------------------------------------------- */
/*                                           TYPES                                                */
/* ---------------------------------------------------------------------------------------------- */

export interface DocumentFetchState {
   documents: DocAiDocument[];
   numberOfDocuments: number;
   isLoading: boolean;
   error: boolean;
}

export interface DocumentFetchParams {
   /**
    * Project whose documents to search. Saved Documents is itself a project.
    * Omitted by doc search, which searches everything the caller can reach.
    */
   projectId?: string;
   /** Current filter state; translated to schema group/code filters. */
   filterState: FilterState;
   /** 1-indexed page. */
   page: number;
   perPage: number;
   /** A column key in any shape — `toSortField` translates it for the API. */
   sortField?: string;
   sortOrder?: SortOrder;
}

export interface FetchDocumentsResult {
   documents: DocAiDocument[];
   total: number;
}

export interface UseDocumentDetailsFetchOptions {
   adapter?: MeasureDocumentAdapter;
}

/* ---------------------------------------------------------------------------------------------- */
/*                                       COMPOSABLE                                               */
/* ---------------------------------------------------------------------------------------------- */

export function useDocumentDetailsFetch(options: UseDocumentDetailsFetchOptions = {}) {
   const adapter = options.adapter ?? createMeasureDocumentAdapter();

   const fetchState = reactive<DocumentFetchState>({
      documents: [],
      numberOfDocuments: 0,
      isLoading: false,
      error: false,
   });

   /** Dimension paths populated in the current result set — the table's columns. */
   const availableFields = ref<string[]>([]);
   /** Bureau/doc-type values available given the other active filters. */
   const availableBureaus = ref<string[]>([]);
   const availableDocTypes = ref<string[]>([]);
   /**
    * Filter keys that could not be mapped to a schema field, so a surface can
    * say so instead of quietly returning results the filters don't describe.
    */
   const unmappableFilterKeys = ref<string[]>([]);

   let abortRequestId = 0;

   function abortRequest(): void {
      // Bump the generation so an in-flight response is ignored on arrival. The
      // adapter shares one ofetch instance, so cancelling via AbortSignal would
      // mean threading a controller through every adapter method.
      abortRequestId += 1;
      // The superseded response returns before its cleanup assignments, so
      // nothing else will clear this — a cancelled fetch would otherwise leave
      // the surface spinning forever.
      fetchState.isLoading = false;
   }

   function buildSort(params: DocumentFetchParams): DocumentSort | undefined {
      if (!params.sortField)
         return undefined;
      // The table's column keys are dimension paths and lifted top-level names;
      // the API takes GROUP.CODE or a measure-level name and *raises* on anything
      // else, so an untranslated key would fail the search rather than sort it
      // oddly. An unsortable column falls back to relevancy.
      const field = toSortField(params.sortField);
      if (!field)
         return undefined;
      return {
         field,
         direction: params.sortOrder === 'asc' ? 'asc' : 'desc',
      };
   }

   function readFacets(facets: { group: string; code: string; buckets: { value: string }[] }[]): void {
      const values = (code: string) =>
         facets
            .filter(f => f.group === 'CLASSIFICATION' && f.code === code)
            .flatMap(f => f.buckets.map(b => b.value))
            .filter(Boolean);

      // Only overwrite when the response actually carried the facet: an empty
      // result set has no buckets, and blanking the dropdowns would strip the
      // user's way back out of a too-narrow filter.
      const bureaus = values('BUREAU');
      if (bureaus.length)
         availableBureaus.value = bureaus;
      const docTypes = values('DOCTYPE');
      if (docTypes.length)
         availableDocTypes.value = docTypes;
   }

   /**
    * Fetch one page of a project's documents.
    *
    * Returns null when the request was superseded or failed; `fetchState.error`
    * distinguishes the two.
    */
   async function fetchDocuments(
      params: DocumentFetchParams,
      fetchOptions: { append?: boolean } = {},
   ): Promise<FetchDocumentsResult | null> {
      const generation = ++abortRequestId;

      fetchState.isLoading = true;
      fetchState.error = false;

      const unmappable: string[] = [];
      const filters: DocumentFilter[] = filterStateToDocumentFilters(params.filterState, {
         onUnmappable: key => unmappable.push(key),
      });

      try {
         const response = await adapter.search({
            keyword: params.filterState.globalSearchKeyword,
            filters,
            sort: buildSort(params),
            page: params.page,
            per_page: params.perPage,
            project_id: params.projectId,
         });

         if (generation !== abortRequestId)
            return null;

         const documents = response.data.map(result => measureToDocAiDocument(result.source));

         fetchState.documents = fetchOptions.append
            ? [...fetchState.documents, ...documents]
            : documents;
         fetchState.numberOfDocuments = response.meta.total;
         availableFields.value = collectDimensionPaths(fetchState.documents);
         unmappableFilterKeys.value = unmappable;
         readFacets(response.facets ?? []);

         fetchState.isLoading = false;
         return { documents, total: response.meta.total };
      }
      catch (e) {
         if (generation !== abortRequestId)
            return null;

         console.error('Failed to fetch documents:', e);
         fetchState.error = true;
         fetchState.isLoading = false;
         return null;
      }
   }

   function $reset(): void {
      abortRequest();
      fetchState.documents = [];
      fetchState.numberOfDocuments = 0;
      fetchState.isLoading = false;
      fetchState.error = false;
      availableFields.value = [];
      availableBureaus.value = [];
      availableDocTypes.value = [];
      unmappableFilterKeys.value = [];
   }

   return {
      fetchState,
      availableFields,
      availableBureaus,
      availableDocTypes,
      unmappableFilterKeys,
      fetchDocuments,
      abortRequest,
      $reset,
   };
}
